export type Provider = "bol" | "booking" | "daisycon" | "awin" | "TODO";

export type LinkResolver = string | ((ctx: Ctx) => string | null);

export interface Ctx {
  placeName?: string;
}

export interface Variant {
  link: LinkResolver;
  text: string;
}

export interface ProductLink {
  item: string;
  provider: Provider;
  variants: Variant[];
}

export type Circumstance =
  | "rain"
  | "snow"
  | "cold"
  | "windy"
  | "headwindLong"
  | "longDistance"
  | "cityLocation"
  | "dark"
  | "sunny"
  | "overnight"
  | "fallback";

const BOL_S = "1516307";
const bol = (searchPath: string, name = "Bol") =>
  `https://partner.bol.com/click/click?p=2&t=url&s=${BOL_S}&f=TXL&url=${encodeURIComponent(
    searchPath
  )}&name=${encodeURIComponent(name)}`;

export const URLS = {
  raincoat: bol("https://www.bol.com/nl/nl/s/?searchtext=regenjas"),
  poncho: bol("https://www.bol.com/nl/nl/s/?searchtext=fietsponcho"),
  gloves: bol("https://www.bol.com/nl/nl/s/?searchtext=handschoenen"),
  glovesWinter: bol("https://www.bol.com/nl/nl/s/?searchtext=winterhandschoenen+fiets"),
  ebike: bol("https://www.bol.com/nl/nl/s/?searchtext=elektrische+fiets"),
  pump: bol(
    "https://www.bol.com/nl/nl/l/fietspompen/18001/",
    "Fietspomp kopen? Alle Fietspompen online"
  ),
  lock: bol(
    "https://www.bol.com/nl/nl/l/fietssloten/17985/",
    "Fietsslot kopen? Alle Fietssloten online"
  ),
  lights: bol("https://www.bol.com/nl/nl/s/?searchtext=fietsverlichting"),
  sunglasses: bol("https://www.bol.com/nl/nl/s/?searchtext=fietsbril+zonnebril"),
  bidon: bol("https://www.bol.com/nl/nl/s/?searchtext=bidon+fiets"),
  helmet: bol("https://www.bol.com/nl/nl/s/?searchtext=fietshelm"),
  panniers: bol("https://www.bol.com/nl/nl/s/?searchtext=fietstassen"),
};

const BOOKING_AID = "TODO";

const buildBookingLink = ({ placeName }: Ctx): string | null => {
  if (BOOKING_AID === "TODO" || !placeName) return null;
  return `https://www.booking.com/searchresults.html?aid=${BOOKING_AID}&ss=${encodeURIComponent(
    placeName
  )}`;
};

export const productLinks: Record<Circumstance, ProductLink> = {
  rain: {
    item: "rain coat",
    provider: "bol",
    variants: [
      {
        link: URLS.raincoat,
        text: "☔️ Het lijkt erop dat het gaat regenen, koop {{hier een regenjas.}}",
      },
      {
        link: URLS.poncho,
        text: "🌧 Droog blijven onderweg? {{Bekijk fietsponcho's.}}",
      },
    ],
  },
  snow: {
    item: "gloves",
    provider: "bol",
    variants: [
      {
        link: URLS.glovesWinter,
        text: "☃️ Het lijkt erop dat het gaat sneeuwen, koop {{hier handschoenen.}}",
      },
      {
        link: URLS.glovesWinter,
        text: "❄️ Warme handen nodig? {{Bekijk winterhandschoenen voor op de fiets.}}",
      },
    ],
  },
  cold: {
    item: "gloves",
    provider: "bol",
    variants: [
      {
        link: URLS.gloves,
        text: "🥶 Het lijkt erop dat het koud wordt, koop {{hier handschoenen.}}",
      },
      {
        link: URLS.gloves,
        text: "🧤 Koude vingers onderweg? {{Bekijk fietshandschoenen.}}",
      },
    ],
  },
  windy: {
    item: "electrical bike",
    provider: "bol",
    variants: [
      {
        link: URLS.ebike,
        text: "🌬 Het gaat flink waaien, al eens aan {{een elektrische fiets}} gedacht?",
      },
      {
        link: URLS.ebike,
        text: "💨 Minder moeite in de wind? {{Zo vind je een elektrische fiets.}}",
      },
    ],
  },
  headwindLong: {
    item: "electrical bike",
    provider: "bol",
    variants: [
      {
        link: URLS.ebike,
        text: "🚴‍♂️💨 Lange rit met tegenwind — {{een elektrische fiets}} scheelt veel kracht.",
      },
      {
        link: URLS.ebike,
        text: "🌬 Tegenwind over een lang stuk? {{Bekijk elektrische fietsen.}}",
      },
    ],
  },
  longDistance: {
    item: "pump",
    provider: "bol",
    variants: [
      {
        link: URLS.pump,
        text: "🗺 Je hebt een flinke tocht voor de boeg! Ben op alles voorbereid met {{deze fietspomp.}}",
      },
      {
        link: URLS.pump,
        text: "🛠 Lange rit gepland? Neem voor de zekerheid {{een fietspomp}} mee.",
      },
    ],
  },
  cityLocation: {
    item: "lock",
    provider: "bol",
    variants: [
      {
        link: URLS.lock,
        text: "🦹‍♂️ Zorg dat je je fiets veilig achterlaat met {{dit slot.}}",
      },
      {
        link: URLS.lock,
        text: "🔒 Fiets even kwijt in de stad? {{Bekijk stevige fietssloten.}}",
      },
    ],
  },
  dark: {
    item: "lights",
    provider: "bol",
    variants: [
      {
        link: URLS.lights,
        text: "🌑 Het is donker — zorg dat je met {{fietsverlichting}} op pad gaat.",
      },
      {
        link: URLS.lights,
        text: "🔦 Goed zichtbaar in het donker? {{Bekijk fietslampen.}}",
      },
    ],
  },
  sunny: {
    item: "sunglasses",
    provider: "bol",
    variants: [
      {
        link: URLS.sunglasses,
        text: "☀️ Lekker zonnetje! {{Een fietsbril}} houdt de zon én het stof uit je ogen.",
      },
      {
        link: URLS.bidon,
        text: "🥤 Warme dag op de fiets geen vergeet {{bidon water}}.",
      },
    ],
  },
  overnight: {
    item: "overnight",
    provider: "booking",
    variants: [
      {
        link: buildBookingLink,
        text: "🏨 Lange tocht! {{Boek een overnachting}} onderweg.",
      },
      {
        link: buildBookingLink,
        text: "🛏 Een meerdaagse rit? {{Vind een hotel op de route.}}",
      },
    ],
  },
  fallback: {
    item: "general",
    provider: "bol",
    variants: [
      {
        link: URLS.ebike,
        text: "🚴 Tip: {{een elektrische fiets}} maakt elke rit makkelijker.",
      },
      {
        link: URLS.helmet,
        text: "🪖 Veilig onderweg? {{Bekijk fietshelmen.}}",
      },
      {
        link: URLS.panniers,
        text: "🎒 Handig voor onderweg: {{fietstassen voor je bagage.}}",
      },
    ],
  },
};
